import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import clsx from 'clsx';
import 'tabbable';
import { getVarName, px, percentToCss } from '../utils/css.js';
import { mergeIgnoringNullish } from '../utils/object.js';
import { useDragV2 } from '../hooks/use-drag/use-drag-v2.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { useFlag } from '../settings/settings.flags.js';
import { observeResizeAll } from '../utils/observe-resize.js';
import { take } from '../utils/ref.js';
import { resolveStyleValue } from '../utils/style-value.js';
import { TRACK_SIZE } from './scroll-container.constants.js';
import { computeScrollPosition, computeScrollSize, handleDrag, handleDragStart } from './scroll-container.utils.js';
import { scrollSize, scrollPercent, content, contentIsolation, scroll, scrollBars, dragging, root, fill, scrollBar } from './scroll-container.css.js';

const scrollSizeVar = getVarName(scrollSize);
const scrollPercentVar = getVarName(scrollPercent);
/**
 * Set up a scrollable region with custom scrollbars that are consistent across browsers and OSes.
 * Ideally this component is used for all scrollable regions in the app so that we can ensure
 * consistent behavior everywhere.
 *
 * ScrollContainer is implemented such that native scroll behavior is used for scroll events, and
 * only the scrollbar drag behavior is custom. This means the ScrollContainer performance should be
 * identical to a native div with `overflow: auto`. The scrollbar size and position is computed in
 * an animation frame so scroll-related properties are not read multiple times per frame.
 *
 * @see https://fpl.figma.design/components/scroll-container/
 */
const ScrollContainer = forwardRef(({ children, styles: s = {}, scroll: scroll$1 = 'y', fill: fill$1 = false, defaultScrollTop, defaultScrollLeft, overscroll, pinBottom, onScroll, onScrollPositionChange, maxHeight, maxWidth, scrollBarYInset = 0, additionalContentRef, ...props }, refIn) => {
    scrollBarYInset = Math.max(scrollBarYInset, 0);
    const ref = useSharedRef(refIn);
    const contentRef = useRef(null);
    const scrollContainerIsolation = useFlag('scrollContainerIsolation');
    const scrollX = scroll$1 === 'x' || scroll$1 === 'both';
    const scrollY = scroll$1 === 'y' || scroll$1 === 'both';
    const lastScrollTop = useRef(0);
    const pinnedToBottom = useRef(false);
    const computation = useRef(0 /* Computation.None */);
    const animationFrame = useRef(null);
    const overscrollHeightRef = useRef(0);
    const scrollBarXRef = useRef(null);
    const scrollBarYRef = useRef(null);
    const [overscrollHeight, setOverscrollHeight] = useState(0);
    const scrollSizeXRef = useRef(scrollX ? 1 : 0);
    const scrollSizeYRef = useRef(scrollY ? 1 : 0);
    const [hasScrollX, setHasScrollX] = useState(scrollX);
    const [hasScrollY, setHasScrollY] = useState(scrollY);
    const setScrollSizeX = useCallback((size) => {
        scrollSizeXRef.current = size;
        scrollBarXRef.current?.style.setProperty(scrollSizeVar, px(size));
        setHasScrollX(size > 0);
    }, []);
    const setScrollSizeY = useCallback((size) => {
        scrollSizeYRef.current = size;
        scrollBarYRef.current?.style.setProperty(scrollSizeVar, px(size));
        setHasScrollY(size > 0);
    }, []);
    // Start these off as `null` if the corresponding scroll axis is enabled so that we only
    // trigger `onScrollPositionChange` when the position is actually known. Otherwise we would
    // always call it with `none` on mount.
    const [scrollPositionX, setScrollPositionX] = useState(scrollX ? null : 'none');
    const [scrollPositionY, setScrollPositionY] = useState(scrollY ? null : 'none');
    const both = scroll$1 === 'both' && hasScrollY && hasScrollX;
    const noScroll = (!scrollX || !hasScrollX) && (!scrollY || !hasScrollY);
    useLayoutEffect(() => {
        const scrollEl = ref.current;
        if (defaultScrollTop != null) {
            scrollEl.scrollTop = defaultScrollTop;
        }
        if (defaultScrollLeft != null) {
            scrollEl.scrollLeft = defaultScrollLeft;
        }
        lastScrollTop.current = scrollEl.scrollTop;
        if (pinBottom) {
            pinnedToBottom.current =
                Math.ceil(scrollEl.scrollTop) + scrollEl.clientHeight >= scrollEl.scrollHeight;
        }
        // We only want to run this once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!scrollPositionX || !scrollPositionY)
            return;
        onScrollPositionChange?.({ x: scrollPositionX, y: scrollPositionY });
    }, [scrollPositionX, scrollPositionY, onScrollPositionChange]);
    useLayoutEffect(() => {
        const scrollEl = ref.current;
        if (!scrollEl)
            return;
        const y = scrollY
            ? computeScrollPosition(scrollEl.scrollTop, scrollEl.scrollHeight, scrollEl.clientHeight)
            : 'none';
        const x = scrollX
            ? computeScrollPosition(scrollEl.scrollLeft, scrollEl.scrollWidth, scrollEl.clientWidth)
            : 'none';
        const jog = y !== 'none' && x !== 'none' ? TRACK_SIZE : 0;
        if (scrollY) {
            setScrollSizeY(computeScrollSize(scrollEl.scrollHeight, scrollEl.clientHeight, jog, scrollEl.clientHeight - scrollBarYInset * 2));
            setScrollPositionY(y);
        }
        if (scrollX) {
            setScrollSizeX(computeScrollSize(scrollEl.scrollWidth, scrollEl.clientWidth, jog));
            setScrollPositionX(x);
        }
        // Only run this once on mount, later updates are handled by the ResizeObserver below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [dragging$1, getDragProps] = useDragV2({
        onGestureStart({ event: e, actions }) {
            const el = e.target;
            // Only drag from a scrollbar track, and re-target pointer capture to that track so events
            // keep coming from it rather than whatever child the pointer landed on.
            return el.hasAttribute('data-fpl-orientation')
                ? actions.accept({ captureTarget: el })
                : actions.cancel();
        },
        onDragStart({ event: e }) {
            const track = e.target;
            const scrollEl = ref.current;
            const box = scrollEl.getBoundingClientRect();
            const isVertical = track.getAttribute('data-fpl-orientation') === 'vertical';
            if (isVertical) {
                const [startPercent, scrollTop] = handleDragStart(both, e.clientY, box.top, box.height, scrollEl.scrollHeight, scrollEl.scrollTop, scrollEl.clientHeight, scrollSizeYRef.current, scrollEl.clientHeight - scrollBarYInset * 2);
                if (scrollTop != null)
                    scrollEl.scrollTop = scrollTop;
                return { box, startPercent };
            }
            const [startPercent, scrollLeft] = handleDragStart(both, e.clientX, box.left, box.width, scrollEl.scrollWidth, scrollEl.scrollLeft, scrollEl.clientWidth, scrollSizeXRef.current);
            if (scrollLeft != null)
                scrollEl.scrollLeft = scrollLeft;
            return { box, startPercent };
        },
        onDrag({ event: e }, { box, startPercent }) {
            const thumb = e.target;
            const scrollEl = ref.current;
            const isVertical = thumb.getAttribute('data-fpl-orientation') === 'vertical';
            if (isVertical) {
                scrollEl.scrollTop = handleDrag(both, box.height, scrollSizeYRef.current, scrollEl.scrollHeight, scrollEl.clientHeight, e.delta.y, startPercent, scrollEl.clientHeight - scrollBarYInset * 2);
            }
            else {
                scrollEl.scrollLeft = handleDrag(both, box.width, scrollSizeXRef.current, scrollEl.scrollWidth, scrollEl.clientWidth, e.delta.x, startPercent);
            }
        },
    });
    const animate = useCallback((comp) => {
        computation.current |= comp;
        if (animationFrame.current)
            return;
        animationFrame.current = requestAnimationFrame(() => {
            const scrollEl = ref.current;
            const contentEl = contentRef.current;
            if (scrollEl && contentEl) {
                const { clientWidth, clientHeight, scrollWidth, scrollHeight } = scrollEl;
                const updateOverscrollMinHeight = () => {
                    if (!overscroll)
                        return;
                    // Only apply overscroll if the mouse is currently over this scroll container. Otherwise
                    // if the content of the scroll container were to shrink by other means, the contents
                    // may seem to disappear if it is smaller than the current scrollTop.
                    const isMouseOver = scrollEl.matches(':hover');
                    const contentsWouldBeHidden = lastScrollTop.current >= contentEl.clientHeight;
                    const minScrollHeight = !isMouseOver || contentsWouldBeHidden || lastScrollTop.current === 0
                        ? 0
                        : // Normally `scrollTop` is an integer and this call is a noop. But when the user
                            // has a positive browser zoom, scrollTop will be fractional. You can't use
                            // fractional px values, so we round up.
                            Math.ceil(lastScrollTop.current) + clientHeight;
                    const minHeight = Math.max(minScrollHeight, contentEl.clientHeight);
                    overscrollHeightRef.current = minHeight;
                    setOverscrollHeight(minHeight);
                };
                // When both scrollbars show, we want to leave a gap where the meet
                const jog = both ? TRACK_SIZE : 0;
                const verticalTrackH = clientHeight - scrollBarYInset * 2;
                const sizeY = scrollY
                    ? computeScrollSize(scrollHeight, clientHeight, jog, verticalTrackH)
                    : 0;
                const sizeX = scrollX ? computeScrollSize(scrollWidth, clientWidth, jog) : 0;
                // Recompute geometry
                if (computation.current & 1 /* Computation.Geometry */) {
                    updateOverscrollMinHeight();
                    if (scrollY)
                        setScrollSizeY(sizeY);
                    if (scrollX)
                        setScrollSizeX(sizeX);
                }
                // Recompute scroll
                if (computation.current & 2 /* Computation.Scroll */) {
                    const { scrollTop, scrollLeft } = scrollEl;
                    const scrollingUp = scrollTop < lastScrollTop.current;
                    lastScrollTop.current = scrollTop;
                    if (scrollY) {
                        const scrollRatio = scrollTop / (scrollHeight - clientHeight);
                        const availableTravel = verticalTrackH - jog - sizeY;
                        scrollBarYRef.current?.style.setProperty(scrollPercentVar, percentToCss((scrollBarYInset + scrollRatio * availableTravel) / clientHeight));
                        setScrollPositionY(computeScrollPosition(scrollTop, scrollHeight, clientHeight));
                    }
                    if (scrollX) {
                        const scrollPercent = scrollLeft / (scrollWidth - clientWidth);
                        const sizePercent = (clientWidth - jog - sizeX) / clientWidth;
                        scrollBarXRef.current?.style.setProperty(scrollPercentVar, percentToCss(scrollPercent * sizePercent));
                        setScrollPositionX(computeScrollPosition(scrollLeft, scrollWidth, clientWidth));
                    }
                    if (overscroll &&
                        scrollingUp &&
                        overscrollHeightRef.current > contentEl.clientHeight) {
                        // If overscroll is currently applied, and the container is being scrolled back up,
                        // then we want to reduce the overscroll bit by bit. This way, the user can scroll
                        // back up, but won't be able to scroll back down.
                        updateOverscrollMinHeight();
                    }
                }
            }
            animationFrame.current = null;
            computation.current = 0 /* Computation.None */;
        });
    }, [both, scrollY, scrollX, overscroll, scrollBarYInset]);
    useEffect(() => {
        const disconnect = observeResizeAll([ref, contentRef, ...(additionalContentRef ? [additionalContentRef] : [])], () => {
            if (pinBottom && pinnedToBottom.current) {
                ref.current.scrollTop = ref.current.scrollHeight;
            }
            animate(1 /* Computation.Geometry */ | 2 /* Computation.Scroll */);
        });
        return () => {
            cancelAnimationFrame(take(animationFrame));
            disconnect();
        };
    }, [animate, pinBottom, additionalContentRef]);
    const rootStyles = resolveStyleValue(s.root);
    const scrollStyles = resolveStyleValue(s.scroll);
    const contentStyles = resolveStyleValue(s.content);
    /* eslint-disable fpl/fpl-data-attr */
    const contentInner = (jsx("div", { ref: contentRef, role: "none", className: clsx(content, contentStyles.className, scrollContainerIsolation && contentIsolation), style: contentStyles.style, children: children }));
    const content$1 = overscroll ? (jsx("div", { role: "none", style: { minHeight: overscrollHeight }, children: contentInner })) : (contentInner);
    /* eslint-enable fpl/fpl-data-attr */
    const rootStyle = mergeIgnoringNullish({ maxHeight, maxWidth }, rootStyles.style);
    const scrollStyle = mergeIgnoringNullish({ maxHeight, maxWidth }, scrollStyles.style);
    return (jsxs("div", { role: "none", className: clsx(root, rootStyles.className, {
            [fill]: fill$1,
        }), style: rootStyle, "data-fpl-scroll-x": scrollPositionX ?? 'none', "data-fpl-scroll-y": scrollPositionY ?? 'none', children: [jsx("div", { ref: ref, role: "none", ...props, className: clsx(scrollStyles.className, scroll[scroll$1]), style: scrollStyle, onScroll: (e) => {
                    if (pinBottom) {
                        const el = e.currentTarget;
                        pinnedToBottom.current = Math.ceil(el.scrollTop) + el.clientHeight >= el.scrollHeight;
                    }
                    animate(2 /* Computation.Scroll */);
                    onScroll?.(e);
                }, children: content$1 }), jsxs("div", { "aria-hidden": true, hidden: noScroll, 
                // Forward wheel events to the scroll container so that scrolling
                // works even when the mouse is over the scrollbar.
                onWheel: (e) => ref.current?.scrollBy(e.deltaX, e.deltaY), ...getDragProps({ className: clsx(scrollBars, { [dragging]: dragging$1 }) }), children: [scrollX && jsx(ScrollBar, { ref: scrollBarXRef, orientation: "horizontal" }), scrollY && (jsx(ScrollBar, { ref: scrollBarYRef, orientation: "vertical" }))] })] }));
});
ScrollContainer.displayName = 'ScrollContainer';
const ScrollBar = forwardRef(function ScrollBar({ orientation }, ref) {
    // The scrollSize CSS var is written imperatively by the parent (see setScrollSizeX/Y) so that
    // per-frame resize updates don't re-render this component.
    return (jsx("div", { "data-fpl-component": "", ref: ref, "data-fpl-orientation": orientation, className: scrollBar }));
});
ScrollBar.displayName = 'ScrollBar';

export { ScrollContainer };
//# sourceMappingURL=scroll-container.js.map
